/**
 * useUploadRecording Hook
 * React Query mutation hook for orchestrating video upload workflow
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { requestMuxUploadUrl, uploadVideoToMux, getMuxAssetStatus } from '@/api/muxApi';
import { createRecording } from '../api/recordingsApi';
import type { Recording } from '../types/recording';

interface UploadRecordingParams {
  file: File;
  title: string;
  description?: string;
  onProgress?: (progress: number) => void;
}

/**
 * Multi-step upload workflow:
 * 1. Request signed upload URL from backend
 * 2. Upload file directly to Mux with progress tracking
 * 3. Poll Mux for processing status
 * 4. Save recording metadata to backend
 */
export function useUploadRecording() {
  const queryClient = useQueryClient();

  return useMutation<Recording, Error, UploadRecordingParams>({
    mutationFn: async ({ file, title, description, onProgress }) => {
      // Step 1: Request upload URL from backend
      const { uploadUrl, uploadId } = await requestMuxUploadUrl(file.name);

      // Step 2: Upload directly to Mux with progress tracking
      await uploadVideoToMux(file, uploadUrl, onProgress);

      // Step 3: Poll for Mux processing status
      // Initial check after upload
      const assetStatus = await getMuxAssetStatus(uploadId);

      // Step 4: Save recording metadata to backend
      const recording = await createRecording({
        title,
        description,
        muxAssetId: uploadId,
        muxPlaybackId: assetStatus.playbackId || '',
        duration: assetStatus.duration,
        fileSize: file.size,
      });

      return recording;
    },
    onSuccess: () => {
      // Invalidate recordings list to refetch with new recording
      queryClient.invalidateQueries({ queryKey: ['recordings'] });
    },
    retry: false, // Don't retry failed uploads
  });
}
